import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Users, ShieldCheck, ArrowRight, Save, RotateCcw, AlertCircle, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOURCE_LABEL = {
  configured: { label: 'Configured', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  inferred:   { label: 'Inferred (NPS)', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  unassigned: { label: 'Unassigned', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
};

const TeamStructureConfig = () => {
  const [rows, setRows] = useState([]);
  const [teamLeaders, setTeamLeaders] = useState([]);
  const [original, setOriginal] = useState({});
  const [dirty, setDirty] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [stats, setStats] = useState({});
  const [newLeader, setNewLeader] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/team-structure`);
      setRows(res.data.managers);
      setTeamLeaders(res.data.teamLeaders);
      const orig = {};
      res.data.managers.forEach(r => { orig[r.manager] = r.teamLeader || ''; });
      setOriginal(orig);
      setDirty({});
      setStats({
        persisted: res.data.persistedCount,
        inferred: res.data.inferredCount,
        unassigned: res.data.unassignedCount,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateLeader = (manager, value) => {
    setDirty(prev => {
      const orig = original[manager] || '';
      const updated = { ...prev };
      if ((value || '') === orig) {
        delete updated[manager];
      } else {
        updated[manager] = value || null;
      }
      return updated;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const mappings = Object.entries(dirty).map(([manager, teamLeader]) => ({ manager, teamLeader }));
      await axios.post(`${API}/team-structure`, mappings);
      setSavedAt(new Date());
      await load();
    } finally {
      setSaving(false);
    }
  };

  const handleResetRow = (manager) => updateLeader(manager, original[manager] || '');

  const addNewLeader = () => {
    if (newLeader.trim() && !teamLeaders.includes(newLeader.trim())) {
      setTeamLeaders(prev => [...prev, newLeader.trim()].sort());
      setNewLeader('');
    }
  };

  const dirtyCount = Object.keys(dirty).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="team-structure-loading">
        <div className="w-8 h-8 border-3 border-[#E5E5E5] border-t-[#171717] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="team-structure-config" className="space-y-6">
      {/* Header */}
      <div className="bg-white border border-[#E5E5E5] rounded-[12px] p-6 shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-[10px] bg-[#171717] flex items-center justify-center">
              <Users className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-[#171717] font-heading">Team Structure</h2>
              <p className="text-xs text-[#737373] mt-1 max-w-2xl leading-relaxed">
                Configure which Team Leader each OC Manager reports to. We&apos;ve pre-populated assignments from NPS history — adjust where needed and save. The Manager and Team Leader Deep Dives use this mapping for all roll-ups.
              </p>
            </div>
          </div>
          {savedAt && (
            <div className="text-[11px] text-emerald-700 flex items-center gap-1.5" data-testid="team-structure-saved">
              <CheckCircle2 className="w-3.5 h-3.5" /> Saved {savedAt.toLocaleTimeString('en-AU')}
            </div>
          )}
        </div>

        <div className="grid grid-cols-3 gap-3 mt-5">
          <Stat label="Configured" value={stats.persisted} cls="text-emerald-700" />
          <Stat label="Inferred from NPS" value={stats.inferred} cls="text-blue-700" />
          <Stat label="Unassigned" value={stats.unassigned} cls="text-amber-700" />
        </div>
      </div>

      {/* Add a TL */}
      <div className="bg-white border border-[#E5E5E5] rounded-[12px] p-4 flex items-center gap-3 flex-wrap">
        <p className="text-xs font-semibold text-[#171717] uppercase tracking-wider">Add a Team Leader</p>
        <input
          data-testid="new-leader-input"
          type="text"
          value={newLeader}
          onChange={(e) => setNewLeader(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && addNewLeader()}
          placeholder="e.g. Gregor Evans"
          className="flex-1 min-w-[200px] px-3 py-2 rounded-[8px] border border-[#E5E5E5] text-sm focus:outline-none focus:border-[#171717]"
        />
        <button
          data-testid="add-leader-btn"
          onClick={addNewLeader}
          className="px-4 py-2 rounded-[8px] bg-[#171717] text-white text-sm font-medium hover:bg-[#000] transition-colors"
        >
          Add
        </button>
        <p className="w-full text-[11px] text-[#A3A3A3]">Adding here lets you assign managers to a new TL. The TL is persisted as soon as a manager is assigned to them.</p>
      </div>

      {/* Mapping table */}
      <div className="bg-white border border-[#E5E5E5] rounded-[12px] overflow-hidden">
        <table className="w-full" data-testid="team-structure-table">
          <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
            <tr>
              <th className="text-left py-3 px-4 text-[10px] font-semibold uppercase tracking-wider text-[#737373]">OC Manager</th>
              <th className="text-left py-3 px-4 text-[10px] font-semibold uppercase tracking-wider text-[#737373]">Team Leader</th>
              <th className="text-left py-3 px-4 text-[10px] font-semibold uppercase tracking-wider text-[#737373]">Source</th>
              <th className="py-3 px-4"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => {
              const isDirty = dirty[row.manager] !== undefined;
              const currentValue = isDirty ? (dirty[row.manager] || '') : (row.teamLeader || '');
              const sourceMeta = SOURCE_LABEL[row.source] || SOURCE_LABEL.unassigned;
              return (
                <tr key={row.manager} className="border-b border-[#F0F0F0] last:border-0 hover:bg-[#FAFAFA]/40">
                  <td className="py-2.5 px-4 text-sm font-medium text-[#171717]">{row.manager}</td>
                  <td className="py-2.5 px-4">
                    <select
                      data-testid={`tl-select-${row.manager.replace(/\s+/g, '-')}`}
                      value={currentValue}
                      onChange={(e) => updateLeader(row.manager, e.target.value)}
                      className={`px-3 py-1.5 rounded-[6px] border text-sm bg-white focus:outline-none focus:border-[#171717] ${isDirty ? 'border-amber-400 bg-amber-50/50' : 'border-[#E5E5E5]'}`}
                    >
                      <option value="">— Unassigned —</option>
                      {teamLeaders.map(tl => <option key={tl} value={tl}>{tl}</option>)}
                    </select>
                  </td>
                  <td className="py-2.5 px-4">
                    <span className={`inline-block px-2 py-0.5 rounded-[4px] border text-[10px] font-semibold ${sourceMeta.cls}`}>
                      {isDirty ? 'Unsaved change' : sourceMeta.label}
                    </span>
                  </td>
                  <td className="py-2.5 px-4 text-right">
                    {isDirty && (
                      <button
                        data-testid={`reset-${row.manager.replace(/\s+/g, '-')}`}
                        onClick={() => handleResetRow(row.manager)}
                        className="text-[11px] text-[#737373] hover:text-[#171717] inline-flex items-center gap-1"
                      >
                        <RotateCcw className="w-3 h-3" /> Reset
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Sticky save bar */}
      {dirtyCount > 0 && (
        <div className="sticky bottom-4 bg-[#171717] text-white rounded-[10px] p-4 shadow-2xl flex items-center justify-between gap-4 z-30">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-4 h-4" strokeWidth={1.5} />
            <p className="text-sm font-medium">{dirtyCount} pending change{dirtyCount === 1 ? '' : 's'}</p>
          </div>
          <button
            data-testid="save-team-structure-btn"
            disabled={saving}
            onClick={handleSave}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-[8px] bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50"
          >
            {saving ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <Save className="w-4 h-4" />}
            Save changes
          </button>
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value, cls }) => (
  <div className="rounded-[8px] border border-[#E5E5E5] bg-[#FAFAFA] px-4 py-3">
    <p className="text-[10px] font-semibold uppercase tracking-wider text-[#737373]">{label}</p>
    <p className={`text-2xl font-bold font-heading ${cls}`}>{value ?? '—'}</p>
  </div>
);

export default TeamStructureConfig;
